import { ExternalLink } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';
import type { WebSource } from '@/services/types/treatment-types';

import { Button } from '../../ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../../ui/catalyst/dialog';
import { Z_INDEX_MODAL_NESTED } from '../../ui/zIndex';

interface EnrichmentSourcesIndicatorProps {
  /** Array of web sources to display */
  sources: WebSource[] | null;
  /** Compact mode shows only count badge, otherwise shows inline links */
  compact?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Displays citation sources for enrichment data.
 * Compact mode shows a badge with count that opens a dialog.
 * Non-compact mode shows inline links to sources.
 */
export const EnrichmentSourcesIndicator = ({
  sources,
  compact = false,
  className,
}: EnrichmentSourcesIndicatorProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!sources || sources.length === 0) {
    return null;
  }

  const handleOpenDialog = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDialogOpen(true);
  };

  // Get hostname from URL for display
  const getHostname = (url: string): string => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  if (compact) {
    return (
      <>
        <button
          type="button"
          onClick={handleOpenDialog}
          aria-label={`View ${sources.length} source${sources.length > 1 ? 's' : ''}`}
          title={`${sources.length} source${sources.length > 1 ? 's' : ''}`}
          className={cn(
            'inline-flex items-center justify-center bg-zinc-100 text-zinc-600 hover:text-zinc-800 hover:bg-zinc-200 rounded-full font-medium text-xs h-5 px-2 transition-colors',
            className,
          )}
        >
          {sources.length} Source{sources.length > 1 ? 's' : ''}
        </button>

        <SourcesDialog
          sources={sources}
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
        />
      </>
    );
  }

  // Inline mode - show links directly
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {sources.map((source, index) => (
        <a
          key={index}
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
          title={source.title}
        >
          <ExternalLink className="h-3 w-3" />
          {getHostname(source.url)}
        </a>
      ))}
    </div>
  );
};

interface SourcesDialogProps {
  sources: WebSource[];
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Check if a title looks malformed (contains markdown syntax, is truncated, etc.)
 */
const isTitleMalformed = (title: string | undefined): boolean => {
  if (!title) return true;
  // Check for markdown link syntax, truncated text, or very short titles
  return (
    title.includes('[') ||
    title.includes('](') ||
    title.length < 5 ||
    title.endsWith('...')
  );
};

/**
 * Get a clean display title for a source
 */
const getCleanTitle = (source: WebSource, index: number): string => {
  if (isTitleMalformed(source.title)) {
    try {
      const hostname = new URL(source.url).hostname.replace('www.', '');
      return hostname;
    } catch {
      return `Source ${index + 1}`;
    }
  }
  return source.title;
};

const SourcesDialog = ({ sources, isOpen, onClose }: SourcesDialogProps) => {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      zIndex={Z_INDEX_MODAL_NESTED}
      nested
    >
      <DialogTitle>
        {sources.length} Source{sources.length > 1 ? 's' : ''}
      </DialogTitle>
      <DialogBody>
        <div className="space-y-3">
          {sources.map((source, index) => (
            <div key={index} className="p-3 border rounded-lg border-zinc-200">
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <div className="text-sm font-medium text-zinc-900 group-hover:text-blue-600 flex items-center gap-1.5">
                  <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                  {getCleanTitle(source, index)}
                </div>
                <div className="text-xs text-zinc-500 mt-1 truncate">
                  {source.url}
                </div>
              </a>
            </div>
          ))}
        </div>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </DialogBody>
    </Dialog>
  );
};
